import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SubscriptionCard from '../components/SubscriptionCard';
import axios from 'axios';
import { validatePassword, PasswordRequirements } from '../utils/validatePassword';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  User,
  Star,
  Fire,
  Trophy,
  Medal,
  Crown,
  Check,
  Lock,
  CreditCard,
  Lightning,
  Handshake,
  Globe,
  PencilSimple,
  Microphone,
  Heart,
  Key,
  Eye,
  EyeSlash,
  Info,
  Camera,
  Trash,
  Sparkle,
  MapPin,
  MusicNote,
  BookBookmark,
  CaretRight
} from '@phosphor-icons/react';
import LevelFigureModal from '../components/LevelFigureModal';
import PushSettingsCard from '../components/PushSettingsCard';
import { avatarUrl } from '../utils/fileUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE_ICONS = {
  star: Star,
  fire: Fire,
  trophy: Trophy,
  heart: Heart,
  microphone: Microphone,
  check: Check,
  pencil: PencilSimple,
  lightning: Lightning,
  crown: Crown,
  handshake: Handshake,
  globe: Globe,
  flame: Fire,
};

const CATEGORY_COLORS = {
  community: { bg: 'bg-blue-100', text: 'text-blue-700', ring: 'ring-blue-200' },
  streak: { bg: 'bg-orange-100', text: 'text-orange-700', ring: 'ring-orange-200' },
  speaking: { bg: 'bg-purple-100', text: 'text-purple-700', ring: 'ring-purple-200' },
  exercises: { bg: 'bg-green-100', text: 'text-green-700', ring: 'ring-green-200' },
  challenges: { bg: 'bg-red-100', text: 'text-red-700', ring: 'ring-red-200' },
  social: { bg: 'bg-teal-100', text: 'text-teal-700', ring: 'ring-teal-200' },
  mastery: { bg: 'bg-amber-100', text: 'text-amber-700', ring: 'ring-amber-200' },
};

const ProfilePage = () => {
  const { user, refreshAuth } = useAuth();
  const [stats, setStats] = useState(null);
  const [allBadges, setAllBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    spanish_level: user?.spanish_level || 'A2'
  });
  const [saving, setSaving] = useState(false);
  const [figureModalOpen, setFigureModalOpen] = useState(false);
  const [modalLevelInfo, setModalLevelInfo] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = React.useRef(null);
  const [albumData, setAlbumData] = useState(null);
  const [nicknameData, setNicknameData] = useState(null);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte eine Bilddatei auswählen');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bild zu groß (max. 5 MB)');
      return;
    }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/users/me/avatar`, fd, {
        withCredentials: true,
      });
      toast.success('Profilbild aktualisiert');
      refreshAuth();
    } catch (err) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAvatarDelete = async () => {
    if (!user?.avatar) return;
    setUploadingAvatar(true);
    try {
      await axios.delete(`${API}/users/me/avatar`, { withCredentials: true });
      toast.success('Profilbild entfernt');
      refreshAuth();
    } catch (err) {
      toast.error('Fehler beim Entfernen');
    } finally {
      setUploadingAvatar(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, badgesRes, albumRes, nicknamesRes] = await Promise.all([
        axios.get(`${API}/stats/me`, { withCredentials: true }),
        axios.get(`${API}/achievements`, { withCredentials: true }),
        axios.get(`${API}/album`, { withCredentials: true }),
        axios.get(`${API}/album/nicknames`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setAllBadges(badgesRes.data);
      setAlbumData(albumRes.data);
      setNicknameData(nicknamesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/users/me`, formData, { withCredentials: true });
      toast.success('Profil aktualisiert');
      setEditing(false);
      refreshAuth();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleSetNickname = async (nickname) => {
    try {
      await axios.patch(`${API}/users/me`, { nickname }, { withCredentials: true });
      toast.success(nickname ? `Apodo: ${nickname}` : 'Apodo entfernt');
      refreshAuth();
      setNicknameData(prev => prev ? { ...prev, current_nickname: nickname } : prev);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleUpgrade = async (packageId) => {
    try {
      const res = await axios.post(`${API}/payments/create-checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      }, { withCredentials: true });
      // Meta Pixel — InitiateCheckout from inside the app upgrade flow.
      try {
        const { trackEvent } = await import('../utils/metaPixel');
        trackEvent('InitiateCheckout', { content_ids: [packageId], content_type: 'product' });
      } catch { /* pixel disabled */ }
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Fehler beim Starten des Upgrades');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-[#AB1C24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const earnedBadges = stats?.badges || stats?.achievements || [];
  const levelInfo = stats?.level || { level: 1, name: 'Principiante', color: '#94a3b8', progress: 0 };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524]">
        Mein Profil
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6" data-testid="profile-card">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
              {/* Avatar */}
              <div className="relative group self-center sm:self-start">
                {user?.avatar ? (
                  <img
                    src={avatarUrl(user.avatar)}
                    alt={user.name}
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-[#AB1C24] to-[#8A151C] flex items-center justify-center text-white text-2xl sm:text-3xl font-bold shadow-lg">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                {user?.is_premium && (
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center shadow-md">
                    <Crown size={18} className="text-white" weight="fill" />
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={handleAvatarChange}
                  className="hidden"
                  data-testid="avatar-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  title="Profilbild ändern"
                  className="absolute -bottom-2 -left-2 w-9 h-9 rounded-full bg-white border border-stone-200 shadow-md flex items-center justify-center text-[#292524] hover:bg-[#AB1C24] hover:text-white hover:border-[#AB1C24] transition-colors disabled:opacity-50"
                  data-testid="upload-avatar-btn"
                >
                  {uploadingAvatar ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera size={16} weight="fill" />
                  )}
                </button>
                {user?.avatar && !uploadingAvatar && (
                  <button
                    type="button"
                    onClick={handleAvatarDelete}
                    title="Profilbild entfernen"
                    className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-white border border-stone-200 shadow-md flex items-center justify-center text-stone-500 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    data-testid="delete-avatar-btn"
                  >
                    <Trash size={14} weight="fill" />
                  </button>
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                {editing ? (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1"
                        data-testid="name-input"
                      />
                    </div>
                    {!['admin', 'teacher'].includes(user?.role) && (
                    <div>
                      <Label htmlFor="level">Spanisch-Niveau</Label>
                      <Select
                        value={formData.spanish_level}
                        onValueChange={(value) => setFormData({ ...formData, spanish_level: value })}
                      >
                        <SelectTrigger className="mt-1" data-testid="level-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A1">A1</SelectItem>
                          <SelectItem value="A2">A2</SelectItem>
                          <SelectItem value="B1">B1</SelectItem>
                          <SelectItem value="B2">B2</SelectItem>
                          <SelectItem value="C1">C1</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    )}
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setEditing(false)}>
                        Abbrechen
                      </Button>
                      <Button
                        onClick={handleSaveProfile}
                        disabled={saving}
                        className="bg-[#AB1C24] hover:bg-[#8A151C]"
                        data-testid="save-profile-btn"
                      >
                        Speichern
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h2 className="font-heading text-2xl font-bold text-[#292524]" data-testid="profile-display-name">
                        {user?.name}
                      </h2>
                      {!['admin', 'teacher'].includes(user?.role) && (
                        <span className={`level-badge ${user?.spanish_level}`}>{user?.spanish_level}</span>
                      )}
                    </div>
                    {user?.nickname && (
                      <p className="text-sm font-medium text-[#AB1C24] mb-1 flex items-center gap-1.5" data-testid="profile-nickname-subtitle">
                        <Sparkle size={14} weight="fill" className="text-amber-500" />
                        {user.nickname}
                      </p>
                    )}
                    <p className="text-[#78716C] mb-1">{user?.email}</p>
                    {/* Level Badge */}
                    <div className="flex items-center gap-2 mb-4">
                      <button
                        type="button"
                        onClick={() => { setModalLevelInfo(levelInfo); setFigureModalOpen(true); }}
                        className="px-3 py-1 rounded-full text-white text-sm font-bold inline-flex items-center gap-1.5 hover:shadow-md hover:scale-105 transition-all cursor-pointer"
                        style={{ backgroundColor: levelInfo.color }}
                        data-testid="user-level-badge"
                        title="Mehr über diese Person erfahren"
                      >
                        Lvl {levelInfo.level} — {levelInfo.name}
                        <Info size={13} weight="fill" />
                      </button>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setEditing(true)}
                      data-testid="edit-profile-btn"
                    >
                      <User size={18} className="mr-2" />
                      Profil bearbeiten
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Level Progress */}
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6" data-testid="level-progress-section">
            <h3 className="font-heading font-semibold text-lg text-[#292524] mb-4">Level-Fortschritt</h3>
            <div className="flex items-center gap-4 mb-3">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-md"
                style={{ backgroundColor: levelInfo.color }}
              >
                {levelInfo.level}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-heading font-semibold text-[#292524]">{levelInfo.name}</span>
                  {levelInfo.next_level && (
                    <button
                      type="button"
                      onClick={() => { setModalLevelInfo(levelInfo.next_level); setFigureModalOpen(true); }}
                      className="text-sm text-[#78716C] hover:text-[#292524] transition-colors cursor-pointer"
                      title="Mehr über die nächste Person erfahren"
                      data-testid="next-level-badge"
                    >
                      Nächstes: <span className="font-medium" style={{ color: levelInfo.next_level.color }}>{levelInfo.next_level.name}</span>
                    </button>
                  )}
                </div>
                <Progress value={levelInfo.progress} className="h-3" />
                <div className="flex justify-between mt-1 text-xs text-[#78716C]">
                  <span>{stats?.points || 0} Punkte</span>
                  {levelInfo.next_level && <span>{levelInfo.next_level.min_points} Punkte</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6" data-testid="stats-section">
            <h3 className="font-heading font-semibold text-lg text-[#292524] mb-4">Statistiken</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-[#F5F5F4] rounded-xl">
                <Star size={24} className="mx-auto mb-2 text-amber-500" weight="fill" />
                <span className="block text-2xl font-bold text-[#292524]">{stats?.points || 0}</span>
                <span className="text-sm text-[#78716C]">Punkte</span>
              </div>
              <div className="text-center p-4 bg-[#F5F5F4] rounded-xl">
                <Fire size={24} className="mx-auto mb-2 text-orange-500" weight="fill" />
                <span className="block text-2xl font-bold text-[#292524]">{stats?.streak || 0}</span>
                <span className="text-sm text-[#78716C]">Tage Streak</span>
              </div>
              <div className="text-center p-4 bg-[#F5F5F4] rounded-xl">
                <Trophy size={24} className="mx-auto mb-2 text-[#6B8E23]" weight="fill" />
                <span className="block text-2xl font-bold text-[#292524]">#{stats?.rank || '-'}</span>
                <span className="text-sm text-[#78716C]">Rang</span>
              </div>
              <div className="text-center p-4 bg-[#F5F5F4] rounded-xl">
                <Check size={24} className="mx-auto mb-2 text-[#5B8C9A]" weight="bold" />
                <span className="block text-2xl font-bold text-[#292524]">{stats?.exercises_completed || 0}</span>
                <span className="text-sm text-[#78716C]">Übungen</span>
              </div>
            </div>
          </div>

          {/* Stripe subscription management. New component so ProfilePage
              doesn't grow unboundedly — handles all 3 states (no sub / active / canceling). */}
          {user?.role === 'user' && <SubscriptionCard />}

          {/* Album Preview — horizontal scroll carousel */}
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6" data-testid="profile-album-section">            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-heading font-semibold text-lg text-[#292524] flex items-center gap-2">
                <BookBookmark size={20} weight="fill" className="text-[#AB1C24]" />
                Álbum
              </h3>
              <Link to="/album" data-testid="view-album-btn">
                <Button variant="outline" size="sm" className="border-[#AB1C24]/40 text-[#AB1C24] hover:bg-[#AB1C24] hover:text-white">
                  Álbum ansehen
                  <CaretRight size={14} className="ml-1" />
                </Button>
              </Link>
            </div>
            {albumData ? (
              <div className="relative">
                <div
                  className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1 snap-x snap-mandatory scroll-smooth"
                  style={{ scrollbarWidth: 'thin' }}
                  data-testid="profile-album-carousel"
                >
                  {(albumData.albums?.all || []).map((item) => {
                    const locked = !item.unlocked;
                    const TypeIcon = item.type === 'city' ? MapPin : item.type === 'song' ? MusicNote : User;
                    return (
                      <div
                        key={`${item.type}-${item.level}`}
                        className={`shrink-0 w-[108px] snap-start rounded-xl border overflow-hidden transition-all ${
                          locked ? 'bg-stone-100 border-stone-200 opacity-70' : 'bg-white border-stone-200 hover:shadow-md hover:scale-[1.03]'
                        }`}
                        data-testid={`album-preview-card-${item.level}`}
                      >
                        <div
                          className="relative h-16 flex items-center justify-center"
                          style={locked ? { background: '#E7E5E4' } : { background: `linear-gradient(135deg, ${item.color}, ${item.color}CC)` }}
                        >
                          {locked ? (
                            <Lock size={22} className="text-stone-400" weight="fill" />
                          ) : (
                            <TypeIcon size={26} className="text-white" weight="fill" />
                          )}
                          <span className="absolute top-1 left-1 bg-white/90 text-[10px] font-bold px-1.5 py-0.5 rounded text-[#292524]">
                            {item.level}
                          </span>
                        </div>
                        <div className="p-2 text-center">
                          <p className={`text-xs font-semibold truncate ${locked ? 'text-stone-400' : 'text-[#292524]'}`}>
                            {item.name || '???'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="py-6 text-center text-[#78716C] text-sm">Lädt...</div>
            )}
          </div>

          {/* Nickname Selector */}
          {nicknameData && nicknameData.nicknames && nicknameData.nicknames.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6" data-testid="nickname-section">
              <div className="flex items-center gap-2 mb-2">
                <Sparkle size={20} weight="fill" className="text-amber-500" />
                <h3 className="font-heading font-semibold text-lg text-[#292524]">Dein Spitzname</h3>
              </div>
              <p className="text-sm text-[#78716C] mb-4">
                Wähle eine freigeschaltete Persönlichkeit als deinen Spitznamen. Er erscheint überall dort, wo dein Name steht.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleSetNickname(null)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                    !nicknameData.current_nickname
                      ? 'bg-[#292524] text-white border-[#292524]'
                      : 'bg-white text-[#292524] border-stone-300 hover:border-[#AB1C24]'
                  }`}
                  data-testid="nickname-none-btn"
                >
                  Echter Name
                </button>
                {nicknameData.nicknames.map((nn) => {
                  const isActive = nicknameData.current_nickname === nn.name;
                  return (
                    <button
                      key={nn.level}
                      onClick={() => handleSetNickname(nn.name)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all flex items-center gap-1.5 ${
                        isActive ? 'text-white shadow-md' : 'bg-white hover:shadow-sm'
                      }`}
                      style={
                        isActive
                          ? { backgroundColor: nn.color, borderColor: nn.color }
                          : { color: nn.color, borderColor: nn.color + '66' }
                      }
                      data-testid={`nickname-btn-${nn.level}`}
                    >
                      <span className="text-xs opacity-80 font-bold">Lv{nn.level}</span>
                      {nn.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Badges Section */}
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6" data-testid="badges-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg text-[#292524]">
                Abzeichen
              </h3>
              <span className="text-sm text-[#78716C]">
                {earnedBadges.length} / {allBadges.length} freigeschaltet
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {allBadges.map((badge) => {
                const unlocked = earnedBadges.includes(badge.id);
                const Icon = BADGE_ICONS[badge.icon] || Star;
                const catColors = CATEGORY_COLORS[badge.category] || CATEGORY_COLORS.community;

                return (
                  <div
                    key={badge.id}
                    className={`relative p-4 rounded-xl border-2 text-center transition-all ${
                      unlocked
                        ? `${catColors.bg} border-transparent ring-2 ${catColors.ring} shadow-sm`
                        : 'bg-gray-50 border-dashed border-gray-200 opacity-60'
                    }`}
                    data-testid={`badge-${badge.id}`}
                  >
                    <div className={`w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-2 ${
                      unlocked ? `${catColors.text} bg-white shadow-sm` : 'bg-gray-200 text-gray-400'
                    }`}>
                      {unlocked ? <Icon size={24} weight="fill" /> : <Lock size={20} />}
                    </div>
                    <p className={`font-medium text-sm ${unlocked ? 'text-[#292524]' : 'text-gray-400'}`}>
                      {badge.name}
                    </p>
                    <p className={`text-xs mt-0.5 ${unlocked ? 'text-[#57534E]' : 'text-gray-400'}`}>
                      {badge.description}
                    </p>
                    <span className={`inline-block mt-2 text-xs font-bold px-2 py-0.5 rounded-full ${
                      unlocked ? `${catColors.bg} ${catColors.text}` : 'bg-gray-200 text-gray-400'
                    }`}>
                      +{badge.points} Pkt
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Progress Overview */}
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6">
            <h3 className="font-heading font-semibold text-lg text-[#292524] mb-4">Fortschritt</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#57534E]">Beiträge</span>
                  <span className="font-medium text-[#292524]">{stats?.posts_count || 0}</span>
                </div>
                <Progress value={Math.min((stats?.posts_count || 0) * 10, 100)} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#57534E]">Sprachaufnahmen</span>
                  <span className="font-medium text-[#292524]">{stats?.voice_count || 0}</span>
                </div>
                <Progress value={Math.min((stats?.voice_count || 0) * 10, 100)} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#57534E]">Übungen</span>
                  <span className="font-medium text-[#292524]">{stats?.exercises_completed || 0}</span>
                </div>
                <Progress value={Math.min((stats?.exercises_completed || 0) * 2, 100)} className="h-2" />
              </div>
            </div>
          </div>

          {/* Change Password Card */}
          <ChangePasswordCard />

          {/* Push Notifications settings */}
          <PushSettingsCard />
        </div>
      </div>
      <LevelFigureModal
        levelInfo={modalLevelInfo}
        open={figureModalOpen}
        onClose={() => setFigureModalOpen(false)}
      />
    </div>
  );
};

const ChangePasswordCard = () => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const pwdErr = validatePassword(next);
    if (pwdErr) return toast.error(pwdErr);
    if (next !== confirm) return toast.error('Passwörter stimmen nicht überein');
    if (!current) return toast.error('Aktuelles Passwort erforderlich');
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: current,
        new_password: next
      }, { withCredentials: true });
      toast.success('Passwort erfolgreich geändert');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Fehler beim Ändern des Passworts');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-4">
        <Key size={22} className="text-[#AB1C24]" weight="fill" />
        <h3 className="font-heading font-semibold text-lg text-[#292524]">Passwort ändern</h3>
      </div>
      <p className="text-sm text-[#78716C] mb-4">
        Wähle ein neues, sicheres Passwort (8–12 Zeichen, Großbuchstabe, Zahl, Sonderzeichen).
      </p>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label className="text-xs text-[#78716C]">Aktuelles Passwort</Label>
          <div className="relative">
            <Input type={showCurrent ? 'text' : 'password'} value={current}
              onChange={e => setCurrent(e.target.value)} required
              className="pr-10" data-testid="current-password-input" autoComplete="current-password" />
            <button type="button" onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
              tabIndex={-1} aria-label="Toggle password visibility">
              {showCurrent ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div>
          <Label className="text-xs text-[#78716C]">Neues Passwort</Label>
          <div className="relative">
            <Input type={showNext ? 'text' : 'password'} value={next}
              onChange={e => setNext(e.target.value)} required minLength={8}
              className="pr-10" data-testid="new-password-input" autoComplete="new-password" />
            <button type="button" onClick={() => setShowNext(!showNext)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
              tabIndex={-1} aria-label="Toggle password visibility">
              {showNext ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <PasswordRequirements password={next} />
        </div>
        <div>
          <Label className="text-xs text-[#78716C]">Neues Passwort bestätigen</Label>
          <Input type={showNext ? 'text' : 'password'} value={confirm}
            onChange={e => setConfirm(e.target.value)} required minLength={6}
            data-testid="confirm-password-input" autoComplete="new-password" />
          {confirm && next && confirm !== next && (
            <p className="text-xs text-red-500 mt-1">Passwörter stimmen nicht überein</p>
          )}
        </div>
        <Button type="submit" disabled={saving} className="w-full bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="save-password-btn">
          {saving ? 'Wird gespeichert...' : 'Passwort ändern'}
        </Button>
      </form>
    </div>
  );
};

export default ProfilePage;

